import {
  GeoCellDisplayStatus,
  GeoTestStatus,
  ICPAdTestConfig
} from '@portal/app/geo-designer/models/ICPAdTestConfig';
import dayjs from 'dayjs';
import { getDateWithoutTimezoneConsideration } from '@portal/app/dashboard/geo-doe-config/shared/dateUtils';
import { CpCellType, RecommendationResult, Status } from '@libs/apis';
import { TestAlertsHelper } from '@portal/app/geo-designer/common/test-alerts.helper';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);

const RESULTS_PENDING_START_OFFSET_DAYS = 1;
const RESULTS_PENDING_WINDOW_DAYS = 7;

interface DisplayStatusResult {
  actualStatus: GeoTestStatus;
  displayStatus: GeoCellDisplayStatus;
}

interface FormattedTestDates {
  today: dayjs.Dayjs;
  startDate: Date;
  endDate: Date;
  reportEndDate: Date;
}

const DEFAULT_DISPLAY_STATUS: DisplayStatusResult = {
  actualStatus: GeoTestStatus.active,
  displayStatus: GeoCellDisplayStatus.none
};

export class TestStatusHelper {
  public static getDisplayStatus = (
    geoTest: ICPAdTestConfig
  ): DisplayStatusResult => {
    const dates = this.getFormattedTestDates(geoTest);

    if (geoTest.status === Status.ACTIVE) {
      return this.getActiveDisplayStatus(geoTest, dates);
    }

    if (geoTest.status === Status.DRAFT) {
      return this.getDraftDisplayStatus(geoTest, dates);
    }

    return DEFAULT_DISPLAY_STATUS;
  };

  private static getActiveDisplayStatus(
    geoTest: ICPAdTestConfig,
    { today, startDate, endDate }: FormattedTestDates
  ): DisplayStatusResult {
    if (today.isBefore(startDate)) {
      return {
        actualStatus: GeoTestStatus.scheduled,
        displayStatus: GeoCellDisplayStatus.scheduled
      };
    }

    if (this.isPendingToLaunch(geoTest)) {
      return {
        actualStatus: GeoTestStatus.scheduled,
        displayStatus: GeoCellDisplayStatus.pendingToLaunch
      };
    }

    if (today.isBetween(startDate, endDate, 'day', '[]')) {
      return {
        actualStatus: GeoTestStatus.active,
        displayStatus: GeoCellDisplayStatus.inProgress
      };
    }

    if (this.isInResultsPendingWindow(today, endDate)) {
      return {
        actualStatus: GeoTestStatus.active,
        displayStatus: GeoCellDisplayStatus.resultsPending
      };
    }

    if (this.isPastResultsPendingWindow(today, endDate)) {
      return {
        actualStatus: GeoTestStatus.finished,
        displayStatus: GeoCellDisplayStatus.finished
      };
    }

    return DEFAULT_DISPLAY_STATUS;
  }

  private static getDraftDisplayStatus(
    geoTest: ICPAdTestConfig,
    { today, startDate }: FormattedTestDates
  ): DisplayStatusResult {
    if (
      this.isActionNeeded(
        geoTest.recommendationResult as RecommendationResult
      ) ||
      today.isAfter(dayjs(startDate).subtract(1, 'day'))
    ) {
      return {
        actualStatus: GeoTestStatus.scheduled,
        displayStatus: GeoCellDisplayStatus.actionNeeded
      };
    }

    if (today.isBefore(startDate)) {
      return {
        actualStatus: GeoTestStatus.scheduled,
        displayStatus: GeoCellDisplayStatus.draft
      };
    }

    return DEFAULT_DISPLAY_STATUS;
  }

  private static isPendingToLaunch(geoTest: ICPAdTestConfig): boolean {
    const testCell = geoTest.cellConfigs.find(
      (cell) => cell.cellType !== CpCellType.BAU
    );
    return TestStatusHelper.isStudyYetToPendingToLaunch(
      geoTest.studyState,
      geoTest.disableSync,
      geoTest.parentId || null,
      testCell?.taxonomyFilters.length || 0
    );
  }

  private static isInResultsPendingWindow(
    today: dayjs.Dayjs,
    endDate: Date
  ): boolean {
    return today.isBetween(
      dayjs(endDate).add(RESULTS_PENDING_START_OFFSET_DAYS, 'day'),
      dayjs(endDate).add(RESULTS_PENDING_WINDOW_DAYS, 'days'),
      'day',
      '[]'
    );
  }

  private static isPastResultsPendingWindow(
    today: dayjs.Dayjs,
    endDate: Date
  ): boolean {
    return today.isAfter(
      dayjs(endDate).add(RESULTS_PENDING_WINDOW_DAYS, 'days')
    );
  }

  private static getFormattedTestDates(
    geoTest: ICPAdTestConfig
  ): FormattedTestDates {
    const today = dayjs();
    const startDate = getDateWithoutTimezoneConsideration(
      geoTest.startDate.toString().split('T')[0]
    );
    const endDate = getDateWithoutTimezoneConsideration(
      geoTest.endDate.toString().split('T')[0]
    );

    const reportEndDate = getDateWithoutTimezoneConsideration(
      geoTest.reportEndDate.toString().split('T')[0]
    );

    return { today, startDate, endDate, reportEndDate };
  }

  private static isActionNeeded = (
    recommendationResult: RecommendationResult
  ): boolean => {
    return (
      TestAlertsHelper.isTacticNotFound(recommendationResult) ||
      TestAlertsHelper.isMsExpired(recommendationResult) ||
      TestAlertsHelper.isWaitingForApproval(recommendationResult)
    );
  };

  public static isStudyYetToPendingToLaunch(
    studyState: { studyId: number; rModTime: string } | null,
    disableSync: 0 | 1,
    parentId: number | null,
    totalInventories: number
  ): boolean {
    if (studyState) {
      return false;
    }
    const isTestContributionOnly = totalInventories === 0;
    const isTestCloned = parentId !== null;
    const isTestManual = disableSync === 1;
    return !(isTestContributionOnly || isTestCloned || isTestManual);
  }
}
